import React from 'react';
import { colors } from '../../styles/colors';
import { Calendar, TrendingUp, Receipt } from 'lucide-react';
import SectionCompact from './SectionCompact';
import ConsultantTable from './ConsultantTable';

function formatCurrencyCompact(value) {
  if (value == null || isNaN(value)) return '-';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

/**
 * TrimestreCard - Carte affichant les données d'un trimestre (Jours, CA, Refacturation)
 *
 * @param {Object} props
 * @param {number} props.quarter - Numéro du trimestre (1-4)
 * @param {number} props.yearFilter - Année sélectionnée
 * @param {Object} props.data - Données du trimestre (sortie de useTrimestreAggregation[q])
 */
function TrimestreCard({ quarter, yearFilter, data }) {
  const colWidth = data.consultantsDuTrimestre.length > 0
    ? `${Math.floor(60 / data.consultantsDuTrimestre.length)}%`
    : '30%';

  return (
    <div style={{
      backgroundColor: colors.text.white,
      borderRadius: '0.75rem',
      overflow: 'hidden',
      border: `2px solid ${colors.primary.teal}`,
      boxShadow: '0 2px 8px rgba(0, 116, 129, 0.06)'
    }}>
      {/* Header du trimestre */}
      <div style={{
        padding: '0.75rem 1rem',
        backgroundColor: colors.primary.teal,
        color: colors.text.white,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{
          fontSize: '1rem',
          fontWeight: '700',
          fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif'
        }}>
          T{quarter} {yearFilter}
        </span>

        <div style={{
          backgroundColor: 'rgba(255,255,255,0.2)',
          borderRadius: '0.375rem',
          padding: '0.25rem 0.5rem',
          fontSize: '0.7rem'
        }}>
          <span>
            TMP: <strong style={{ color: colors.primary.gold }}>
              {data.tmpValue ? formatCurrencyCompact(data.tmpValue) : '—'}
            </strong>
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div style={{ padding: '0.75rem' }}>

        {/* Section Jours */}
        <SectionCompact
          icon={<Calendar size={14} color={colors.primary.teal} />}
          title="Jours"
          bgColor={colors.secondary.lightTeal}
        >
          <ConsultantTable
            monthsData={data.monthsData}
            consultants={data.consultantsDuTrimestre}
            valueKey="days"
            totals={data.totalsByConsultant}
            totalGlobal={data.trimesterTotalDays}
            formatValue={(v) => v || 0}
            colWidth={colWidth}
          />
        </SectionCompact>

        {/* Section CA */}
        <SectionCompact
          icon={<TrendingUp size={14} color={colors.hover.successDark} />}
          title="Chiffre d'affaires"
          bgColor={colors.background.lightGreen}
        >
          <ConsultantTable
            monthsData={data.monthsData}
            consultants={data.consultantsDuTrimestre}
            valueKey="ca"
            totals={data.totalsByConsultant}
            totalGlobal={data.trimesterTotalCA}
            formatValue={formatCurrencyCompact}
            colWidth={colWidth}
          />
        </SectionCompact>

        {/* Section Refacturation */}
        <SectionCompact
          icon={<Receipt size={14} color={colors.primary.gold} />}
          title="Refacturation"
          bgColor={colors.background.lightYellow}
          isLast
        >
          {data.tmpValue ? (
            <ConsultantTable
              monthsData={data.monthsData}
              consultants={data.consultantsDuTrimestre}
              valueKey="refac"
              totals={data.refacByConsultant}
              totalGlobal={data.trimesterRefac}
              formatValue={formatCurrencyCompact}
              colWidth={colWidth}
              tmpValue={data.tmpValue}
            />
          ) : (
            <div style={{
              padding: '0.5rem',
              textAlign: 'center',
              color: colors.secondary.mediumGray,
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}>
              TMP non disponible
            </div>
          )}
        </SectionCompact>

      </div>
    </div>
  );
}

export default TrimestreCard;
